import React, { MouseEvent, FormEvent, ChangeEvent, useState } from "react";
import "./SaveOfferForm.css";
import Text from "src/components/Input/Text/Text";
import { useConfiguration } from "src/contexts/ConfigurationContext";
import Checkbox from "src/components/Input/Checkbox/Checkbox";
import TextArea from "src/components/Input/TextArea/TextArea";
import { usePopup } from "src/contexts/PopupContext";
import { useLoader } from "src/contexts/LoaderContext";
import { useView } from "src/contexts/ViewContext";

const nlPostcodeRegex = /^[1-9][0-9]{3}\s?[A-Za-z]{2}$/;
const phoneRegex = /^\+?\d{10}$/;

const SaveOfferForm: React.FC = () => {
  const { request } = useConfiguration();
  const {
    isSaveOfferFormVisible,
    setIsSaveOfferFormVisible,
    setIsResultPopupVisible,
    setResultPopupText,
    setResultPopupImage,
  } = usePopup();
  const {
    setIsWhiteSceneLoaderVisible,
    setIsMainLoaderVisible,
    setMainLoaderText,
  } = useLoader();

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    street: "",
    house_number: "",
    city: "",
    postcode: "",
    comment: "",
  });

  const [errors, setErrors] = useState<Partial<typeof formData>>({});
  const [isCommentVisible, setIsCommentVisible] = useState(false);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckboxChange = (checked: boolean) => {
    setIsCommentVisible(checked);
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<typeof formData> = {};

    for (const key in formData) {
      if (key !== "comment" && !formData[key as keyof typeof formData]) {
        newErrors[key as keyof typeof formData] = "Dit veld is verplicht.";
      }
    }

    if (formData.phone && !phoneRegex.test(formData.phone)) {
      newErrors.phone = "Veldwaarde moet toegestane invoer te vullen.";
    }

    if (formData.postcode && !nlPostcodeRegex.test(formData.postcode)) {
      newErrors.postcode = 'Postcode is verplicht. (e.g. "1234 AB").';
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    const formElement = event.currentTarget;
    const formData = new FormData(formElement);
    const formObject: Record<string, any> = {};

    setIsSaveOfferFormVisible(false);
    setIsWhiteSceneLoaderVisible(true);
    setMainLoaderText("Werk in uitvoering. We slaan nu uw overkapping op.");
    setIsMainLoaderVisible(true);

    try {
      formData.forEach((value, key) => {
        formObject[key] = String(value);
      });
      const response = await request.addOffer(formObject);
      setIsWhiteSceneLoaderVisible(false);
      setIsMainLoaderVisible(false);
      setResultPopupText(response.data.message);
      setResultPopupImage(response.data.image || "");
      setIsResultPopupVisible(true);
    } catch (error) {
      console.error("Error:", error);
      setIsSaveOfferFormVisible(false);

      setTimeout(() => {
        setIsWhiteSceneLoaderVisible(false);
        setIsMainLoaderVisible(false);
        setResultPopupText("Er is iets misgegaan. Probeer het later opnieuw.");
        setIsResultPopupVisible(true);
      }, 1000);
    }
  };

  const handleOverlayClick = (event: MouseEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    setIsSaveOfferFormVisible(false);
  };

  const renderField = (name: keyof typeof formData, label: string) => (
    <div className="save-offer-form-field">
      <Text
        name={name}
        label={label}
        content={formData[name]}
        onChange={handleChange}
      />
      {errors[name] && <div className="error-msg">{errors[name]}</div>}
    </div>
  );

  const renderTextAreaField = (
    name: keyof typeof formData,
    placeholder: string
  ) => (
    <div className="save-offer-form-field">
      <TextArea
        name={name}
        placeholder={placeholder}
        content={formData[name]}
        onChange={handleChange}
      />
      {errors[name] && <div className="error-msg">{errors[name]}</div>}
    </div>
  );

  const dataFormHeader = {
    imageUrl: "../images/logo.svg",
    descriptionHeader: "Mijn overkapping opslaan + offerte opstellen",
    textDescription:
      "Vul je gegevens in en wij sturen je per email de samengestelde overkapping toe en nemen contact met je op om de offerte door te spreken.",
  };

  return (
    <>
      {isSaveOfferFormVisible && (
        <div className="save-offer-form" onClick={handleOverlayClick}>
          <div className="save-offer-form-overlay">
            <div
              className="close-form"
              onClick={() => setIsSaveOfferFormVisible(false)}
            >
              ×
            </div>
            <div className="save-offer-form-header">
              <img
                src={dataFormHeader.imageUrl}
                alt="buitenlevenxxl"
                // width="200"
                height="50"
              />
            </div>
            <div className="save-offer-form-overlay-text">
              <h2>{dataFormHeader.descriptionHeader}</h2>
              <p>{dataFormHeader.textDescription}</p>
            </div>
            <form id="saveOfferForm" method="post" onSubmit={handleSubmit}>
              {renderField("first_name", "Voornaam")}
              {renderField("last_name", "Achternaam")}
              {renderField("email", "E-mailadres")}
              {renderField("phone", "Telefoonnummer")}
              {renderField("street", "Straatnaam")}
              {renderField("house_number", "Huisnr")}
              {renderField("postcode", "Postcode")}
              {renderField("city", "Plaatsnaam")}
              <div className="save-offer-form-comment not-required">
                <Checkbox
                  checked={isCommentVisible}
                  onChange={handleCheckboxChange}
                  label="Ik wil nog wat veranderen aan de overkapping"
                />
              </div>
              {isCommentVisible &&
                renderTextAreaField(
                  "comment",
                  "Heeft u nog een andere aanmerking of wilt u graag iets anders zien in de overkapping vermeld het dan hier"
                )}
              <button type="submit">Opslaan</button>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default SaveOfferForm;
